import React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  RadioGroup,
} from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IQuizFormInput, IQuizFormValues } from '../../../quiz.types';
import { StyledFieldError } from '../../../styles';
import { DropdownInner } from './DropdownInner';
import { getChoicesHint } from './helpers';
import {
  StyledCheckboxChoicesHint,
  StyledChoiceCheckbox,
  StyledChoiceLabel,
  StyledChoiceRadio,
} from './styles';

const DROPDOWN_THRESHOLD = 10;

// ---------------------------------------------------------------------------
// Choice — radio / checkbox / dropdown
// ---------------------------------------------------------------------------
export function ChoiceInput({ input }: { input: IQuizFormInput }): JSX.Element {
  const { t } = useTranslation();
  const { control, errors, trigger } = useFormContext<IQuizFormValues>();
  const fieldName = `answers.${input.id}.choiceValues`;
  const choices = input.formInputChoices;
  // Single choice if maxAnswer === 1 (covers the case minAnswer === 1 && maxAnswer === 1).
  // Every other case (null, > 1) → multi-choice.
  const isSingleChoice = input.maxAnswer === 1;
  const minAnswer = input.minAnswer ?? (input.isRequired ? 1 : 0);
  const maxAnswer = input.maxAnswer ?? choices.length;
  // Use a dropdown when there are more than DROPDOWN_THRESHOLD choices,
  // regardless of how many the user may select.
  const useDropdown = choices.length > DROPDOWN_THRESHOLD;
  const error = (errors?.answers as any)?.[input.id]?.choiceValues?.message as
    | string
    | undefined;

  const validateChoice = (val: string[]): true | string => {
    const count = val?.length ?? 0;
    const isExact =
      input.minAnswer !== null &&
      input.maxAnswer !== null &&
      input.minAnswer === input.maxAnswer;

    // Non-required + empty → always valid (question can be skipped entirely).
    if (!input.isRequired && count === 0) return true;

    // Below minimum (covers both "at least N" and "exactly N" cases).
    if (minAnswer > 0 && count < minAnswer) {
      if (!input.isRequired) {
        return isExact
          ? (t('quiz.form.errors.exactChoicesOrSkip', {
              count: minAnswer,
            }) as string)
          : (t('quiz.form.errors.minChoicesOrSkip', {
              min: minAnswer,
            }) as string);
      }
      return isExact
        ? (t('quiz.form.errors.exactChoicesRequired', {
            count: minAnswer,
          }) as string)
        : (t('quiz.form.errors.minChoicesRequired', {
            min: minAnswer,
          }) as string);
    }

    // Above maximum.
    if (count > maxAnswer) {
      return t('quiz.form.errors.maxChoices', { max: maxAnswer }) as string;
    }

    return true;
  };

  if (useDropdown) {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={[]}
        rules={{ validate: validateChoice }}
        render={({ onChange, value }) => (
          <DropdownInner
            input={input}
            selected={Array.isArray(value) ? value : []}
            isSingleChoice={isSingleChoice}
            minAnswer={minAnswer}
            maxAnswer={maxAnswer}
            error={error}
            onChange={(val) => {
              onChange(val);
              trigger(fieldName as any);
            }}
          />
        )}
      />
    );
  }

  if (isSingleChoice) {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={[]}
        rules={{ validate: validateChoice }}
        render={({ onChange, value }) => {
          const selected: string =
            Array.isArray(value) && value.length > 0 ? value[0] : '';
          return (
            <FormControl component="fieldset" error={!!error}>
              <RadioGroup
                value={selected}
                onChange={(_e, newValue) => {
                  onChange([newValue]);
                  trigger(fieldName as any);
                }}
              >
                {choices.map((c) => (
                  <FormControlLabel
                    key={c['@id']}
                    value={c['@id']}
                    control={<StyledChoiceRadio color="primary" />}
                    label={
                      <StyledChoiceLabel>{c.title}</StyledChoiceLabel>
                    }
                  />
                ))}
              </RadioGroup>
              {error && <StyledFieldError>{error}</StyledFieldError>}
            </FormControl>
          );
        }}
      />
    );
  }

  return (
    <Controller
      name={fieldName}
      control={control}
      defaultValue={[]}
      rules={{ validate: validateChoice }}
      render={({ onChange, value }) => {
        const selected: string[] = (value as string[]) ?? [];
        const toggle = (iri: string) => {
          let next: string[];
          if (selected.includes(iri)) {
            next = selected.filter((v) => v !== iri);
          } else if (selected.length < maxAnswer) {
            next = [...selected, iri];
          } else {
            return; // Silently block if maxAnswer already reached
          }
          onChange(next);
          trigger(fieldName as any);
        };
        return (
          <FormControl component="fieldset" error={!!error}>
            <StyledCheckboxChoicesHint variant="caption" color="textSecondary">
              {getChoicesHint(
                t,
                input.minAnswer ?? null,
                input.maxAnswer ?? null,
              )}
            </StyledCheckboxChoicesHint>
            <FormGroup>
              {choices.map((c) => (
                <FormControlLabel
                  key={c['@id']}
                  control={
                    <StyledChoiceCheckbox
                      color="primary"
                      checked={selected.includes(c['@id'])}
                      onChange={() => toggle(c['@id'])}
                      disabled={
                        !selected.includes(c['@id']) &&
                        selected.length >= maxAnswer
                      }
                    />
                  }
                  label={
                    <StyledChoiceLabel>{c.title}</StyledChoiceLabel>
                  }
                />
              ))}
            </FormGroup>
            {error && <StyledFieldError>{error}</StyledFieldError>}
          </FormControl>
        );
      }}
    />
  );
}
